/**
 * Фича «Комната по клику»: прямоугольная комната заданного размера одним кликом.
 * Размер задают в окошке «Новая комната» (`CreateRoomDialog`), оно же включает инструмент.
 *
 * Размер задают в чистоте — от стены до стены, как его меряют рулеткой и как считается
 * площадь комнаты (`room.inner`). Поэтому оси стен отодвинуты наружу на половину толщины:
 * комната 3000 × 2000 из перегородок 120 мм — это оси 3120 × 2120, а площадь ровно 6,00 м².
 * Начерти такие стены по осям 3000 × 2000 — в чистоте вышло бы 2880 × 1880 = 5,41 м².
 *
 * За курсором едет призрак комнаты (центр — с шагом сетки), клик ставит стены
 * выбранного типа, толщины, высоты и материала — той же операцией, что и черчение
 * (`addWall`): к стенам, в которые угол упрётся, комната примкнёт Т-стыком. Придвинутая
 * к существующей стене комната прилипает к её грани и берёт эту стену себе — вторую рядом
 * не строит (`roomLayout`). Стены не пересекаются и не накладываются друг на друга: если
 * комната всё равно задевает существующие, призрак красный и по клику ничего не строится.
 * Поставили — инструмент снова «Выбор»; Esc — передумали.
 */
import { useEffect, useState } from "react";
import type { PointerEvent } from "react";
import {
	effectiveWallGraph,
	findWallConflict,
	initialWallGraph,
	WALL_EDGE_TOLERANCE,
	type WallGraph,
} from "@/shared/plan";
import { snapTo, type Pt } from "@/shared/lib/geometry";
import { addWallKeepingRooms } from "../roomCoverings";
import { roomLayout, type RoomWallSegment } from "../roomLayout";
import { useProjectStore } from "@/shared/store";
import { Tool, useEditorStore } from "@/widgets/editor";

/** Призрак комнаты: стены, которые встанут, чистый контур и можно ли ставить. */
export interface RoomGhost {
	/** Стены, которые построятся; стороны из существующих стен сюда не входят */
	segments: RoomWallSegment[];
	/** Углы чистого контура — по нему размеры и площадь */
	inner: Pt[];
	width: number;
	depth: number;
	/** Стены будут несущими — призрак красный, как они на чертеже */
	bearing: boolean;
	/** Задевает существующие стены — ставить нельзя */
	blocked: boolean;
}

const BLOCKED_MESSAGE = "Комната задевает существующие стены — поставьте её на свободное место";

interface Options {
	toWorld: (clientX: number, clientY: number) => Pt;
}

export const usePlaceRoom = ({ toWorld }: Options) => {
	const tool = useEditorStore((s) => s.tool);
	const roomSize = useEditorStore((s) => s.roomSize);
	const wallSettings = useEditorStore((s) => s.wallSettings);
	const planStage = useEditorStore((s) => s.planStage);
	const gridStep = useEditorStore((s) => s.gridStep);
	const rawGraph = useProjectStore((s) => s.project.layers.walls);
	const graph =
		planStage === "initial" || planStage === "demolition"
			? initialWallGraph(rawGraph)
			: effectiveWallGraph(rawGraph);
	const isMountedStage =
		planStage !== "initial" && planStage !== "demolition" && planStage !== "all";
	const [center, setCenter] = useState<Pt | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const active = tool === Tool.Room;

	// Подсказка о неудаче гаснет сама
	useEffect(() => {
		if (notice === null) return;
		const timer = setTimeout(() => setNotice(null), 2600);
		return () => clearTimeout(timer);
	}, [notice]);

	const { thickness } = wallSettings;
	/** Те же допуски, что и у построения стены в сторе, — иначе проверка разойдётся с результатом */
	const conflictOptions = {
		nodeTolerance: gridStep * 1.5,
		edgeTolerance: WALL_EDGE_TOLERANCE,
	};

	/** Раскладка комнаты с центром `at` и задевает ли она чужие стены. */
	const layoutAt = (plan: WallGraph, at: Pt) => {
		const layout = roomLayout(plan, at, roomSize.width, roomSize.depth, thickness);
		const blocked = layout.segments.some(
			(segment) =>
				findWallConflict(plan, segment.from, segment.to, {
					...conflictOptions,
					thickness: segment.thickness,
				}) !== null,
		);
		return { ...layout, blocked };
	};

	const preview = ((): RoomGhost | null => {
		if (!active || !center) return null;
		const { inner, segments, blocked } = layoutAt(graph, center);
		const { width, depth } = roomSize;
		return { segments, inner, width, depth, bearing: wallSettings.bearing, blocked };
	})();

	/** Центр комнаты под курсором: с шагом сетки, Alt — без привязки. */
	const centerAt = (e: PointerEvent<Element>): Pt => {
		const world = toWorld(e.clientX, e.clientY);
		return e.altKey ? world : { x: snapTo(world.x, gridStep), y: snapTo(world.y, gridStep) };
	};

	/** Курсор над холстом; null — ушёл */
	const handlePointerMove = (e: PointerEvent<Element> | null) => {
		if (!active) return;
		setCenter(e ? centerAt(e) : null);
	};

	/** Клик инструментом «Комната»: ставит стены. true — клик забран. */
	const handlePointerDown = (e: PointerEvent<Element>): boolean => {
		if (!active || e.button !== 0) return false;
		e.stopPropagation();
		e.preventDefault();
		const rawPlan = useProjectStore.getState().project.layers.walls;
		const plan =
			planStage === "initial" || planStage === "demolition"
				? initialWallGraph(rawPlan)
				: effectiveWallGraph(rawPlan);
		const layout = layoutAt(plan, centerAt(e));
		if (layout.blocked) {
			setNotice(BLOCKED_MESSAGE);
			return true;
		}
		useProjectStore.getState().pushHistory();
		for (const segment of layout.segments) {
			addWallKeepingRooms(segment.from, segment.to, {
				...wallSettings,
				mounted: isMountedStage,
				thickness: segment.thickness,
				snapTolerance: gridStep * 1.5,
			});
		}
		setCenter(null);
		const editor = useEditorStore.getState();
		editor.setSelection(null);
		editor.setTool(Tool.Select);
		return true;
	};

	return {
		active,
		preview,
		hint: notice ?? (preview?.blocked ? BLOCKED_MESSAGE : null),
		handlePointerMove,
		handlePointerDown,
	};
};
